import type {
  Baseline,
  ChangeCounts,
  ChangeResult,
  Checkpoint,
  CheckVerdict,
  MeasurementVerdict,
} from "./schema"

/**
 * When and how an applied change is judged: code only, against the baseline stored for it. Each checkpoint is due at
 * the first weekly run at least its days after the clock starts.
 */

export const DAY_MS = 86_400_000

/** The days a run drafts from: a weekly run's week, or the seven days before a press. */
export const DRAFTING_DAYS = 7

/** The weekly run, as the cron in `vercel.json` schedules it: Wednesdays at 06:00 UTC. */
export const WEEKLY_RUN = { weekday: 3, hourUtc: 6 } as const

/** «Wednesday», as copy names the weekly run's day. */
export function weeklyRunDay(): string {
  // 4 January 1970 was a Sunday, the weekday 0.
  return new Date(Date.UTC(1970, 0, 4 + WEEKLY_RUN.weekday)).toLocaleString("en-US", {
    weekday: "long",
    timeZone: "UTC",
  })
}

export const CHECKPOINT_DAYS: Record<Checkpoint, number> = { early: 6, final: 13, extension: 27 }

/** The baseline: the 28 days before the days the change was drafted from. */
export const BASELINE_DAYS = 28
/** Below this the baseline takes in the drafted week too. */
export const MIN_BASELINE_REQUESTS = 8
/** How long a change learned while the agent was silent waits for the agent's next answer on its question. */
export const CLOCK_WAIT_DAYS = 56
/** Runs a checkpoint waits for reviews still pending in its window. */
export const MAX_REVIEW_WAITS = 2
/** A waiting draft closes at the first run this long after it was drafted. */
export const WAITING_DAYS = 27

/** The early checkpoint looks only for harm, at a stricter bar. */
const RULES: Record<Checkpoint, { minAfter: number; minBaseline: number; alpha: number; judgesHelped: boolean }> = {
  early: { minAfter: 6, minBaseline: 1, alpha: 0.01, judgesHelped: false },
  final: { minAfter: 8, minBaseline: 8, alpha: 0.05, judgesHelped: true },
  extension: { minAfter: 8, minBaseline: 8, alpha: 0.05, judgesHelped: true },
}

export type CheckpointOutcome =
  | { kind: "take_back" }
  | { kind: "final"; verdict: MeasurementVerdict }
  | { kind: "next"; checkpoint: Checkpoint }

/** What made a change worse: more requests left with neither an answer nor a person, or fewer resolved alone. */
export type Harm = "left_without" | "fewer_resolved"

export function checkpointAt(clockStart: Date, checkpoint: Checkpoint): Date {
  return new Date(clockStart.getTime() + CHECKPOINT_DAYS[checkpoint] * DAY_MS)
}

/** The first weekly run at or after `at`. */
export function firstRunAt(at: Date): Date {
  const runThatDay = Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate(), WEEKLY_RUN.hourUtc)
  const daysAhead = (WEEKLY_RUN.weekday - at.getUTCDay() + 7) % 7
  const run = runThatDay + daysAhead * DAY_MS
  return new Date(run < at.getTime() ? run + 7 * DAY_MS : run)
}

/** The run that closes a draft nobody decided. */
export function waitingClosesAt(createdAt: Date): Date {
  return firstRunAt(new Date(createdAt.getTime() + WAITING_DAYS * DAY_MS))
}

/** The checkpoint after those done; `null` once the extension is. */
export function nextCheckpoint(done: Checkpoint[]): Checkpoint | null {
  if (done.includes("extension")) return null
  if (done.includes("final")) return "extension"
  return done.includes("early") ? "final" : "early"
}

/** The latest checkpoint due at `now`: a change first looked at late skips the early one. */
export function dueCheckpoint(clockStart: Date, done: Checkpoint[], now: Date): Checkpoint | null {
  const isDue = (checkpoint: Checkpoint) => checkpointAt(clockStart, checkpoint) <= now
  if (done.includes("extension")) return null
  if (done.includes("final")) return isDue("extension") ? "extension" : null
  if (isDue("final")) return "final"
  return !done.includes("early") && isDue("early") ? "early" : null
}

/**
 * Worse is more requests left with neither an answer nor a person, or — for a change that does not tighten a rule —
 * fewer resolved by the agent alone. Helped is more resolved alone, never read off a widened baseline.
 */
export function judgeCheckpoint(
  checkpoint: Checkpoint,
  baseline: Baseline,
  after: ChangeCounts,
  tightens: boolean
): CheckVerdict {
  const rule = RULES[checkpoint]
  if (tooFew(rule, baseline, after)) return "too_few"
  if (harmFound(rule, baseline, after, tightens)) return "worse"

  const moreResolved = fisherOneSided(
    after.resolvedByAgent,
    after.requests,
    baseline.resolvedByAgent,
    baseline.requests
  )
  return rule.judgesHelped && !baseline.widened && moreResolved <= rule.alpha ? "helped" : "no_change"
}

/** The harm a checkpoint judged `worse` found, the first rule first; `null` where it found none. */
export function harmOf(
  checkpoint: Checkpoint,
  baseline: Baseline,
  after: ChangeCounts,
  tightens: boolean
): Harm | null {
  const rule = RULES[checkpoint]
  return tooFew(rule, baseline, after) ? null : harmFound(rule, baseline, after, tightens)
}

type Rule = (typeof RULES)[Checkpoint]

function tooFew(rule: Rule, baseline: Baseline, after: ChangeCounts): boolean {
  return after.requests < rule.minAfter || baseline.requests < rule.minBaseline
}

function harmFound(rule: Rule, baseline: Baseline, after: ChangeCounts, tightens: boolean): Harm | null {
  if (fisherOneSided(after.leftWithout, after.requests, baseline.leftWithout, baseline.requests) <= rule.alpha) {
    return "left_without"
  }
  const fewerResolved = fisherOneSided(baseline.resolvedByAgent, baseline.requests, after.resolvedByAgent, after.requests)
  return !tightens && fewerResolved <= rule.alpha ? "fewer_resolved" : null
}

export function outcomeOf(checkpoint: Checkpoint, verdict: CheckVerdict, baseline: Baseline): CheckpointOutcome {
  if (verdict === "worse") return { kind: "take_back" }
  switch (checkpoint) {
    case "early":
      return { kind: "next", checkpoint: "final" }
    case "final":
      if (verdict !== "too_few") return { kind: "final", verdict }
      // A baseline too small now stays too small: only the after side grows.
      return baseline.requests < RULES.extension.minBaseline
        ? { kind: "final", verdict: "not_enough" }
        : { kind: "next", checkpoint: "extension" }
    case "extension":
      return { kind: "final", verdict: verdict === "too_few" ? "not_enough" : verdict }
    default: {
      const exhaustive: never = checkpoint
      return exhaustive
    }
  }
}

/** The change check's direction, else the drafter's word that it touches a limit; without either, it does not. */
export function tightensRule(result: ChangeResult | null): boolean {
  const direction = result?.check?.direction
  return direction ? direction === "tightens" : result?.draft?.touchesLimit === true
}

/**
 * One-sided Fisher's exact test: how likely a share of `hitsA` in `totalA` at least this far above `hitsB` in
 * `totalB` is by chance alone. Exact at the small counts one question gets in a week.
 */
export function fisherOneSided(hitsA: number, totalA: number, hitsB: number, totalB: number): number {
  if (totalA === 0 || totalB === 0) return 1
  const total = totalA + totalB
  const hits = hitsA + hitsB
  const logFactorial = logFactorials(total)
  const logChoose = (n: number, k: number) => logFactorial[n] - logFactorial[k] - logFactorial[n - k]

  const all = logChoose(total, totalA)
  let p = 0
  for (let x = hitsA; x <= Math.min(hits, totalA); x += 1) {
    p += Math.exp(logChoose(hits, x) + logChoose(total - hits, totalA - x) - all)
  }
  return Math.min(1, p)
}

function logFactorials(n: number): number[] {
  const values = [0]
  for (let i = 1; i <= n; i += 1) values.push(values[i - 1] + Math.log(i))
  return values
}
